import type { Body, Equality, Geom, Joint, JointUUID, Scene, Sensor, Tendon } from "../types/scene";

export type DeploymentWarning = {
  code: string;
  message: string;
  objectType?: string;
  objectUuid?: string;
  objectName?: string;
};

export type DeploymentNameMaps = {
  body: Record<string, string>;
  joint: Record<string, string>;
  actuator: Record<string, string>;
};

export type DeploymentMimicJoint = {
  equalityUuid: string;
  equalityName?: string;
  sourceJointUuid: string;
  sourceJointName: string;
  urdfSourceJointName: string;
  targetJointUuid: string;
  targetJointName: string;
  urdfTargetJointName: string;
  multiplier: number;
  offset: number;
};

export type DeploymentConstraintMetadata = {
  urdfMimicJoints: DeploymentMimicJoint[];
  unsupportedEqualityConstraints: Array<{
    uuid: string;
    name?: string;
    type?: string;
    objtype?: string;
    obj1?: string | null;
    obj2?: string | null;
    reason: string;
    data?: number[] | null;
  }>;
  ballJoints: Array<{
    uuid: string;
    name: string;
    urdfJointName: string;
    bodyUuid?: string;
    bodyName?: string;
    urdfLinkName?: string;
    originalDofs: number;
    exportedDofs: number;
    collapsedDofs: number;
    note: string;
  }>;
};

export type DeploymentMeshAssetRef = {
  filename: string;
  scale?: number[];
};

export type UrdfExportResult = {
  urdf: string;
  warnings: DeploymentWarning[];
  names: DeploymentNameMaps;
  constraints: DeploymentConstraintMetadata;
};

const SYNTHETIC_WORLD_LINK = "world";

const xmlEscape = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const finiteNumber = (value: unknown, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const vector = (value: unknown, length: number, fallback: number[] = []): number[] => {
  const source = Array.isArray(value) ? value : [];
  return Array.from({ length }, (_, index) => finiteNumber(source[index], fallback[index] ?? 0));
};

const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) return "0";
  return Number(value.toFixed(10)).toString();
};

const formatVector = (values: number[]): string => values.map(formatNumber).join(" ");

const sanitizeUrdfName = (value: unknown, fallback: string): string => {
  const raw = String(value || fallback || "").trim() || fallback;
  let out = raw.replace(/[^A-Za-z0-9_]/g, "_");
  out = out.replace(/_+/g, "_").replace(/^_+|_+$/g, "");
  if (!out) out = fallback;
  if (!/^[A-Za-z_]/.test(out)) out = `n_${out}`;
  return out;
};

const makeUniqueName = (used: Set<string>, preferred: string): string => {
  let candidate = preferred;
  let suffix = 1;
  while (used.has(candidate)) {
    candidate = `${preferred}_${suffix}`;
    suffix += 1;
  }
  used.add(candidate);
  return candidate;
};

const DEFAULT_EQUALITY_POLYCOEF: [number, number, number, number, number] = [0, 1, 0, 0, 0];
const EPSILON = 1e-9;
const URDF_VELOCITY_LIMIT_PLACEHOLDER = 1;
const URDF_LOCKED_LIMIT: [number, number] = [0, 0];

const normalizeEqualityPolycoef = (
  value: unknown
): [number, number, number, number, number] | null => {
  if (value == null) return DEFAULT_EQUALITY_POLYCOEF;
  if (!Array.isArray(value)) return null;
  const values = value.map(Number);
  if (values.length !== 5 || values.some((entry) => !Number.isFinite(entry))) return null;
  return values as [number, number, number, number, number];
};

const isScalarUrdfJoint = (
  joint: Joint | undefined
): joint is Joint & { kind: "hinge" | "slide" } =>
  !!joint && (joint.kind === "hinge" || joint.kind === "slide");

const pushWarningOnce = (warnings: DeploymentWarning[], warning: DeploymentWarning): void => {
  if (
    warnings.some((entry) => entry.code === warning.code && entry.objectUuid === warning.objectUuid)
  ) {
    return;
  }
  warnings.push(warning);
};

const hasNonZeroVector = (value: unknown, length: number): boolean =>
  vector(value, length).some((entry) => Math.abs(entry) > EPSILON);

const quatWxyzToRpy = (quat: unknown): [number, number, number] => {
  const [wRaw, xRaw, yRaw, zRaw] = vector(quat, 4, [1, 0, 0, 0]);
  const length = Math.hypot(wRaw, xRaw, yRaw, zRaw) || 1;
  const w = wRaw / length;
  const x = xRaw / length;
  const y = yRaw / length;
  const z = zRaw / length;
  const roll = Math.atan2(2 * (w * x + y * z), 1 - 2 * (x * x + y * y));
  const sinp = 2 * (w * y - z * x);
  const pitch = Math.abs(sinp) >= 1 ? (Math.sign(sinp) * Math.PI) / 2 : Math.asin(sinp);
  const yaw = Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
  return [roll, pitch, yaw];
};

const buildOriginXml = (position: unknown, orientation: unknown, indent: string): string =>
  `${indent}<origin xyz="${formatVector(vector(position, 3))}" rpy="${formatVector(quatWxyzToRpy(orientation))}"/>\n`;

const buildNameMaps = (scene: Scene): DeploymentNameMaps => {
  const usedBodyNames = new Set<string>([SYNTHETIC_WORLD_LINK]);
  const usedJointNames = new Set<string>();
  const usedActuatorNames = new Set<string>();
  const body: Record<string, string> = {};
  const joint: Record<string, string> = {};
  const actuator: Record<string, string> = {};

  Object.values(scene.bodies || {}).forEach((entry) => {
    if (entry?.uuid)
      body[entry.uuid] = makeUniqueName(
        usedBodyNames,
        sanitizeUrdfName(entry.name || entry.uuid, `body_${entry.uuid}`)
      );
  });
  Object.values(scene.joints || {}).forEach((entry) => {
    if (entry?.uuid)
      joint[entry.uuid] = makeUniqueName(
        usedJointNames,
        sanitizeUrdfName(entry.name || entry.uuid, `joint_${entry.uuid}`)
      );
  });
  Object.values(scene.actuators || {}).forEach((entry) => {
    if (entry?.uuid)
      actuator[entry.uuid] = makeUniqueName(
        usedActuatorNames,
        sanitizeUrdfName(entry.name || entry.uuid, `actuator_${entry.uuid}`)
      );
  });
  return { body, joint, actuator };
};

const getParentBodyMap = (bodies: Record<string, Body>): Record<string, string> => {
  const parentByChild: Record<string, string> = {};
  Object.values(bodies || {}).forEach((body) => {
    (body?.children || []).forEach((childUuid) => {
      if (typeof childUuid === "string" && childUuid) parentByChild[childUuid] = body.uuid;
    });
  });
  return parentByChild;
};

const getJointBodyMap = (scene: Scene): Record<string, Body> => {
  const bodyByJointUuid: Record<string, Body> = {};
  Object.values(scene.bodies || {}).forEach((body) => {
    (body?.joints || []).forEach((jointUuid) => {
      if (typeof jointUuid === "string" && jointUuid) bodyByJointUuid[jointUuid] = body;
    });
  });
  return bodyByJointUuid;
};

const getWorldBodyUuids = (scene: Scene): string[] => {
  const bodies = (scene.bodies || {}) as Record<string, Body | undefined>;
  const roots = (scene.worldBodies || [])
    .map((entry) => (typeof entry === "string" ? entry : entry?.uuid))
    .filter((uuid: unknown): uuid is string => typeof uuid === "string" && !!bodies[uuid]);
  if (roots.length) return Array.from(new Set(roots));
  const parentByChild = getParentBodyMap(scene.bodies || {});
  return Object.keys(scene.bodies || {}).filter((uuid) => !parentByChild[uuid]);
};

const findBodyJoint = (body: Body, scene: Scene, warnings: DeploymentWarning[]): Joint | null => {
  const jointRefs = Array.isArray(body.joints) ? body.joints.filter(Boolean) : [];
  if (jointRefs.length === 0) return null;
  if (jointRefs.length > 1) {
    warnings.push({
      code: "multi_joint_body_fixed",
      message: `Body "${body.name}" has multiple MJCF joints; URDF v1 exports its parent attachment as fixed.`,
      objectType: "body",
      objectUuid: body.uuid,
      objectName: body.name,
    });
    return null;
  }
  const joint = scene.joints?.[jointRefs[0]];
  if (!joint) return null;
  if (joint.kind === "free" || joint.kind === "ball") {
    warnings.push({
      code: `${joint.kind}_joint_fixed`,
      message: `Joint "${joint.name}" is a MuJoCo ${joint.kind} joint; URDF v1 exports its parent attachment as fixed.`,
      objectType: "joint",
      objectUuid: joint.uuid,
      objectName: joint.name,
    });
    return null;
  }
  return joint;
};

const getJointType = (joint: Joint | null): "fixed" | "revolute" | "continuous" | "prismatic" => {
  if (!joint) return "fixed";
  if (joint.kind === "slide") return "prismatic";
  if (joint.kind === "hinge") return joint.useLimits === "false" ? "continuous" : "revolute";
  return "fixed";
};

const buildConstraintMetadata = (
  scene: Scene,
  names: DeploymentNameMaps,
  warnings: DeploymentWarning[]
): DeploymentConstraintMetadata => {
  const urdfMimicJoints: DeploymentMimicJoint[] = [];
  const unsupportedEqualityConstraints: DeploymentConstraintMetadata["unsupportedEqualityConstraints"] =
    [];
  const ballJoints: DeploymentConstraintMetadata["ballJoints"] = [];
  const mimicTargets = new Set<string>();
  const bodyByJointUuid = getJointBodyMap(scene);

  Object.values(scene.joints || {}).forEach((joint) => {
    if (joint?.kind !== "ball") return;
    const body = bodyByJointUuid[joint.uuid];
    ballJoints.push({
      uuid: joint.uuid,
      name: joint.name,
      urdfJointName:
        names.joint[joint.uuid] ||
        sanitizeUrdfName(joint.name || joint.uuid, `joint_${joint.uuid}`),
      bodyUuid: body?.uuid,
      bodyName: body?.name,
      urdfLinkName: body?.uuid ? names.body[body.uuid] : undefined,
      originalDofs: 3,
      exportedDofs: 0,
      collapsedDofs: 3,
      note: "URDF v1 exports MuJoCo ball joints as fixed parent attachments. A future exporter can approximate this as three serial revolute joints.",
    });
  });

  Object.values(scene.equality || {}).forEach((equality: Equality) => {
    const entryBase = {
      uuid: String(equality?.uuid || ""),
      name: equality?.name ? String(equality.name) : undefined,
      type: equality?.type ? String(equality.type) : undefined,
      objtype: equality?.objtype ? String(equality.objtype) : undefined,
      obj1: equality?.obj1 ? String(equality.obj1) : null,
      obj2: equality?.obj2 ? String(equality.obj2) : null,
      data: Array.isArray(equality?.data)
        ? equality.data.map(Number).filter((value: number) => Number.isFinite(value))
        : null,
    };

    if (equality?.active === false) {
      unsupportedEqualityConstraints.push({ ...entryBase, reason: "inactive_equality_constraint" });
      return;
    }
    if (equality?.type !== "joint" || equality?.objtype !== "joint") {
      unsupportedEqualityConstraints.push({
        ...entryBase,
        reason: "urdf_has_no_general_equality_constraint",
      });
      return;
    }

    const targetJointUuid = typeof equality.obj1 === "string" ? equality.obj1 : "";
    const sourceJointUuid = typeof equality.obj2 === "string" ? equality.obj2 : "";
    const sourceJoint = scene.joints?.[sourceJointUuid as JointUUID];
    const targetJoint = scene.joints?.[targetJointUuid as JointUUID];
    if (!isScalarUrdfJoint(sourceJoint) || !isScalarUrdfJoint(targetJoint)) {
      unsupportedEqualityConstraints.push({
        ...entryBase,
        reason: "mimic_requires_hinge_or_slide_joints",
      });
      return;
    }

    const polycoef = normalizeEqualityPolycoef(equality.data);
    if (!polycoef) {
      unsupportedEqualityConstraints.push({ ...entryBase, reason: "invalid_polycoef" });
      return;
    }
    if (
      Math.abs(polycoef[2]) > EPSILON ||
      Math.abs(polycoef[3]) > EPSILON ||
      Math.abs(polycoef[4]) > EPSILON
    ) {
      unsupportedEqualityConstraints.push({
        ...entryBase,
        reason: "nonlinear_polycoef_not_representable_as_urdf_mimic",
      });
      return;
    }
    if (Math.abs(polycoef[1]) <= EPSILON) {
      warnings.push({
        code: "mimic_multiplier_zero",
        message: `Joint equality "${equality.name || equality.uuid}" has zero linear multiplier; URDF emits a fixed-offset mimic relation that some consumers may not handle consistently.`,
        objectType: "equality",
        objectUuid: equality.uuid,
        objectName: equality.name,
      });
    }
    if (mimicTargets.has(targetJointUuid)) {
      unsupportedEqualityConstraints.push({
        ...entryBase,
        reason: "target_joint_already_has_urdf_mimic",
      });
      warnings.push({
        code: "duplicate_mimic_target_not_exported",
        message: `Joint equality "${equality.name || equality.uuid}" targets a joint that already has a URDF mimic relation; preserving it in metadata only.`,
        objectType: "equality",
        objectUuid: equality.uuid,
        objectName: equality.name,
      });
      return;
    }

    mimicTargets.add(targetJointUuid);
    urdfMimicJoints.push({
      equalityUuid: String(equality.uuid),
      equalityName: equality.name ? String(equality.name) : undefined,
      sourceJointUuid,
      sourceJointName: sourceJoint.name,
      urdfSourceJointName:
        names.joint[sourceJoint.uuid] ||
        sanitizeUrdfName(sourceJoint.name || sourceJoint.uuid, `joint_${sourceJoint.uuid}`),
      targetJointUuid,
      targetJointName: targetJoint.name,
      urdfTargetJointName:
        names.joint[targetJoint.uuid] ||
        sanitizeUrdfName(targetJoint.name || targetJoint.uuid, `joint_${targetJoint.uuid}`),
      multiplier: finiteNumber(polycoef[1], 1),
      offset: finiteNumber(polycoef[0], 0),
    });
  });

  return { urdfMimicJoints, unsupportedEqualityConstraints, ballJoints };
};

const getFiniteJointRange = (
  joint: Joint & { kind: "hinge" | "slide" }
): [number, number] | null => {
  const range = Array.isArray(joint.range) ? joint.range.map(Number) : [];
  if (range.length < 2 || !Number.isFinite(range[0]) || !Number.isFinite(range[1])) return null;
  if (range[1] < range[0]) return null;
  return [range[0], range[1]];
};

const getFiniteRangeMagnitude = (range: unknown): number | null => {
  const values = Array.isArray(range) ? range.map(Number) : [];
  if (values.length < 2 || !Number.isFinite(values[0]) || !Number.isFinite(values[1])) return null;
  return Math.max(Math.abs(values[0]), Math.abs(values[1]));
};

const buildJointEffortLimitMap = (scene: Scene): Record<string, number> => {
  const effortByJointUuid: Record<string, number> = {};
  Object.values(scene.actuators || {}).forEach((actuator) => {
    const jointUuid = actuator?.joint;
    if (!jointUuid) return;
    const effort = getFiniteRangeMagnitude(actuator.forcerange);
    if (effort == null) return;
    effortByJointUuid[jointUuid] = Math.max(effortByJointUuid[jointUuid] || 0, effort);
  });
  return effortByJointUuid;
};

const buildJointLimitXml = (
  joint: Joint | null,
  effortByJointUuid: Record<string, number>,
  warnings: DeploymentWarning[],
  indent: string
): string => {
  if (
    !joint ||
    (joint.kind !== "hinge" && joint.kind !== "slide") ||
    getJointType(joint) === "continuous"
  )
    return "";
  const finiteRange = getFiniteJointRange(joint);
  let range = finiteRange;
  if (!range) {
    range = URDF_LOCKED_LIMIT;
    pushWarningOnce(warnings, {
      code:
        joint.kind === "slide" && joint.useLimits === "false"
          ? "unbounded_slide_limit_locked"
          : "joint_limit_defaulted_locked",
      message:
        joint.kind === "slide" && joint.useLimits === "false"
          ? `Slide joint "${joint.name}" is unlimited in MuJoCo; URDF requires finite prismatic limits, so the deployment URDF locks it at 0. Use model.xml or deployment metadata for full-fidelity motion.`
          : `Joint "${joint.name}" has no finite limit range; URDF requires finite limits, so the deployment URDF locks it at 0.`,
      objectType: "joint",
      objectUuid: joint.uuid,
      objectName: joint.name,
    });
  }

  const jointEffort = getFiniteRangeMagnitude(joint.actfrcrange);
  const actuatorEffort = effortByJointUuid[joint.uuid];
  const hasForceRange =
    jointEffort != null || (Number.isFinite(actuatorEffort) && actuatorEffort >= 0);
  const effort = jointEffort ?? actuatorEffort ?? 0;
  if (!hasForceRange) {
    pushWarningOnce(warnings, {
      code: "joint_effort_limit_unknown",
      message: `Joint "${joint.name}" has no finite actuator force range; URDF limit effort is emitted as 0 and deployment_config.json should be reviewed before hardware use.`,
      objectType: "joint",
      objectUuid: joint.uuid,
      objectName: joint.name,
    });
  }
  pushWarningOnce(warnings, {
    code: "joint_velocity_limit_placeholder",
    message: `Joint "${joint.name}" has no source velocity limit in the Critter scene; URDF limit velocity uses placeholder ${URDF_VELOCITY_LIMIT_PLACEHOLDER}.`,
    objectType: "joint",
    objectUuid: joint.uuid,
    objectName: joint.name,
  });
  return `${indent}<limit lower="${formatNumber(range[0])}" upper="${formatNumber(range[1])}" effort="${formatNumber(effort)}" velocity="${formatNumber(URDF_VELOCITY_LIMIT_PLACEHOLDER)}"/>\n`;
};

const buildJointDynamicsXml = (joint: Joint | null, indent: string): string => {
  if (!joint || (joint.kind !== "hinge" && joint.kind !== "slide")) return "";
  const damping = finiteNumber(joint.damping, 0);
  const friction = finiteNumber(joint.frictionloss, 0);
  const attrs: string[] = [];
  if (damping > 0) attrs.push(`damping="${formatNumber(damping)}"`);
  if (friction > 0) attrs.push(`friction="${formatNumber(friction)}"`);
  return attrs.length ? `${indent}<dynamics ${attrs.join(" ")}/>\n` : "";
};

const buildInertialXml = (body: Body, indent: string): string => {
  if (!body.inertia) return "";
  const inertia = body.inertia;
  const diag = vector(inertia.diagonal, 3, [1e-6, 1e-6, 1e-6]);
  const full = Array.isArray(inertia.fullinertia) ? vector(inertia.fullinertia, 6) : null;
  return (
    `${indent}<inertial>\n` +
    buildOriginXml(inertia.position, inertia.orientation, `${indent}  `) +
    `${indent}  <mass value="${formatNumber(Math.max(finiteNumber(inertia.mass, 1), 1e-9))}"/>\n` +
    `${indent}  <inertia ixx="${formatNumber(full ? full[0] : diag[0])}" ixy="${formatNumber(full ? full[3] : 0)}" ixz="${formatNumber(full ? full[4] : 0)}" iyy="${formatNumber(full ? full[1] : diag[1])}" iyz="${formatNumber(full ? full[5] : 0)}" izz="${formatNumber(full ? full[2] : diag[2])}"/>\n` +
    `${indent}</inertial>\n`
  );
};

const geomGeometryXml = (
  geom: Geom,
  meshRefs: Record<string, DeploymentMeshAssetRef>,
  warnings: DeploymentWarning[],
  indent: string
): string => {
  const size = vector(geom.size, 3, [0.05, 0.05, 0.05]);
  if (geom.kind === "box")
    return `${indent}<box size="${formatVector(size.map((value) => value * 2))}"/>\n`;
  if (geom.kind === "sphere")
    return `${indent}<sphere radius="${formatNumber(Math.max(size[0], 0))}"/>\n`;
  if (geom.kind === "cylinder")
    return `${indent}<cylinder radius="${formatNumber(Math.max(size[0], 0))}" length="${formatNumber(Math.max(size[1] * 2, 0))}"/>\n`;
  if (geom.kind === "capsule") {
    warnings.push({
      code: "capsule_approximated_as_cylinder",
      message: `Capsule geom "${geom.name || geom.uuid}" is approximated as a cylinder in URDF.`,
      objectType: "geom",
      objectUuid: geom.uuid,
      objectName: geom.name,
    });
    return `${indent}<cylinder radius="${formatNumber(Math.max(size[0], 0))}" length="${formatNumber(Math.max(size[1] * 2, 0))}"/>\n`;
  }
  if (geom.kind === "mesh") {
    const ref = geom.mesh ? meshRefs[geom.mesh] : null;
    if (!ref?.filename) {
      warnings.push({
        code: "mesh_path_missing",
        message: `Mesh geom "${geom.name || geom.uuid}" has no resolved mesh file for URDF export.`,
        objectType: "geom",
        objectUuid: geom.uuid,
        objectName: geom.name,
      });
      return "";
    }
    if (/\.msh(?:$|[?#])/i.test(ref.filename)) {
      pushWarningOnce(warnings, {
        code: "mesh_format_may_be_unsupported",
        message: `Mesh geom "${geom.name || geom.uuid}" references "${ref.filename}", a format many URDF/ROS consumers do not support directly.`,
        objectType: "geom",
        objectUuid: geom.uuid,
        objectName: geom.name,
      });
    }
    const scale = Array.isArray(ref.scale)
      ? ` scale="${formatVector(vector(ref.scale, 3, [1, 1, 1]))}"`
      : "";
    return `${indent}<mesh filename="${xmlEscape(ref.filename)}"${scale}/>\n`;
  }
  warnings.push({
    code: "geom_skipped",
    message: `Geom "${geom.name || geom.uuid}" of type "${geom.kind}" is not supported in URDF v1 and was skipped.`,
    objectType: "geom",
    objectUuid: geom.uuid,
    objectName: geom.name,
  });
  return "";
};

const buildGeomBlockXml = (
  tag: "visual" | "collision",
  geom: Geom,
  meshRefs: Record<string, DeploymentMeshAssetRef>,
  warnings: DeploymentWarning[],
  indent: string
): string => {
  const geometry = geomGeometryXml(geom, meshRefs, warnings, `${indent}    `);
  if (!geometry) return "";
  return `${indent}<${tag}>\n${buildOriginXml(geom.position, geom.orientation, `${indent}  `)}${indent}  <geometry>\n${geometry}${indent}  </geometry>\n${indent}</${tag}>\n`;
};

const buildLinkXml = (
  body: Body,
  names: DeploymentNameMaps,
  meshRefs: Record<string, DeploymentMeshAssetRef>,
  warnings: DeploymentWarning[]
): string => {
  let xml = `  <link name="${xmlEscape(names.body[body.uuid] || body.uuid)}">\n`;
  const inertialXml = buildInertialXml(body, "    ");
  if (!inertialXml) {
    pushWarningOnce(warnings, {
      code: "link_inertial_missing",
      message: `Link "${body.name || body.uuid}" has no explicit inertial data in the scene; URDF exports the link without <inertial>.`,
      objectType: "body",
      objectUuid: body.uuid,
      objectName: body.name,
    });
  }
  xml += inertialXml;
  const hasExplicitVisualGeoms = (body.geoms || []).some((geom) => geom?.visualOnly === true);
  (body.geoms || []).forEach((geom) => {
    if (geom.visualOnly || !hasExplicitVisualGeoms) {
      xml += buildGeomBlockXml("visual", geom, meshRefs, warnings, "    ");
    }
    if (!geom.visualOnly) xml += buildGeomBlockXml("collision", geom, meshRefs, warnings, "    ");
  });
  return `${xml}  </link>\n`;
};

const buildAttachmentJointXml = (
  body: Body,
  parentLinkName: string,
  scene: Scene,
  names: DeploymentNameMaps,
  warnings: DeploymentWarning[],
  mimicByJointUuid: Record<string, DeploymentMimicJoint>,
  effortByJointUuid: Record<string, number>
): string => {
  const joint = findBodyJoint(body, scene, warnings);
  const type = getJointType(joint);
  const jointName = joint
    ? names.joint[joint.uuid] || sanitizeUrdfName(joint.name || joint.uuid, `joint_${joint.uuid}`)
    : sanitizeUrdfName(
        `fixed_${parentLinkName}_${names.body[body.uuid] || body.uuid}`,
        "fixed_joint"
      );
  let xml = `  <joint name="${xmlEscape(jointName)}" type="${type}">\n`;
  xml += buildOriginXml(body.position, body.orientation, "    ");
  xml += `    <parent link="${xmlEscape(parentLinkName)}"/>\n`;
  xml += `    <child link="${xmlEscape(names.body[body.uuid] || body.uuid)}"/>\n`;
  if (joint && (joint.kind === "hinge" || joint.kind === "slide")) {
    if (hasNonZeroVector(joint.position, 3)) {
      pushWarningOnce(warnings, {
        code: "joint_anchor_offset_approximated",
        message: `Joint "${joint.name}" has a non-zero MuJoCo anchor position. URDF v1 cannot represent that anchor without changing link frames, so the deployment URDF keeps the child link frame and records this approximation.`,
        objectType: "joint",
        objectUuid: joint.uuid,
        objectName: joint.name,
      });
    }
    xml += `    <axis xyz="${formatVector(vector(joint.axis, 3, [0, 0, 1]))}"/>\n`;
    xml += buildJointLimitXml(joint, effortByJointUuid, warnings, "    ");
    xml += buildJointDynamicsXml(joint, "    ");
    const mimic = mimicByJointUuid[joint.uuid];
    if (mimic) {
      xml += `    <mimic joint="${xmlEscape(mimic.urdfSourceJointName)}" multiplier="${formatNumber(mimic.multiplier)}" offset="${formatNumber(mimic.offset)}"/>\n`;
    }
  }
  return `${xml}  </joint>\n`;
};

export function buildUrdfFromScene(
  scene: Scene,
  options?: { modelName?: string; meshRefs?: Record<string, DeploymentMeshAssetRef> }
): UrdfExportResult {
  const warnings: DeploymentWarning[] = [];
  const names = buildNameMaps(scene);
  const constraints = buildConstraintMetadata(scene, names, warnings);
  const mimicByJointUuid = Object.fromEntries(
    constraints.urdfMimicJoints.map((mimic) => [mimic.targetJointUuid, mimic])
  );
  const effortByJointUuid = buildJointEffortLimitMap(scene);
  const roots = getWorldBodyUuids(scene);
  const meshRefs = options?.meshRefs || {};
  const modelName = sanitizeUrdfName(
    options?.modelName || scene.name || "critter_robot",
    "critter_robot"
  );
  let urdf = `<?xml version="1.0" encoding="UTF-8"?>\n<robot name="${xmlEscape(modelName)}">\n`;
  urdf += `  <link name="${SYNTHETIC_WORLD_LINK}"/>\n`;
  Object.values(scene.bodies || {}).forEach((body) => {
    if (body) urdf += buildLinkXml(body, names, meshRefs, warnings);
  });
  const parentByChild = getParentBodyMap(scene.bodies || {});
  const bodies = (scene.bodies || {}) as Record<string, Body | undefined>;
  roots.forEach((rootUuid) => {
    const body = bodies[rootUuid];
    if (body)
      urdf += buildAttachmentJointXml(
        body,
        SYNTHETIC_WORLD_LINK,
        scene,
        names,
        warnings,
        mimicByJointUuid,
        effortByJointUuid
      );
  });
  Object.values(scene.bodies || {}).forEach((body) => {
    if (!body || roots.includes(body.uuid)) return;
    urdf += buildAttachmentJointXml(
      body,
      names.body[parentByChild[body.uuid]] || SYNTHETIC_WORLD_LINK,
      scene,
      names,
      warnings,
      mimicByJointUuid,
      effortByJointUuid
    );
  });
  Object.values(scene.tendons || {}).forEach((tendon: Tendon) =>
    warnings.push({
      code: "tendon_not_exported_to_urdf",
      message: `Tendon "${tendon?.name || tendon?.uuid}" is preserved in model.xml but not represented in URDF v1.`,
      objectType: "tendon",
      objectUuid: tendon?.uuid,
      objectName: tendon?.name,
    })
  );
  constraints.unsupportedEqualityConstraints.forEach((equality) =>
    warnings.push({
      code: "equality_not_exported_to_urdf",
      message: `Equality constraint "${equality.name || equality.uuid}" is preserved in model.xml/deployment metadata but not represented in URDF v1 (${equality.reason}).`,
      objectType: "equality",
      objectUuid: equality.uuid,
      objectName: equality.name,
    })
  );
  Object.values(scene.sensors || {}).forEach((sensor: Sensor) =>
    warnings.push({
      code: "sensor_not_exported_to_urdf",
      message: `Sensor "${sensor?.name || sensor?.uuid}" is preserved in model.xml but not represented in URDF v1.`,
      objectType: "sensor",
      objectUuid: sensor?.uuid,
      objectName: sensor?.name,
    })
  );
  urdf += "</robot>\n";
  return { urdf, warnings, names, constraints };
}
